import React from 'react'
import styled from 'styled-components'
import {Box, BoxProps, Text, TextProps, themeGet, SxProp, sx} from '../..'
import {Appearance} from '../../components/AtlasLozenge/types'
import {RuleFilterTagProps} from './types'

/**
 * Provides a visual indicator that a user has added a script or filter to an item. Use with a RuleFilter.
 */
export const RuleFilterTag = ({
  children,
  validationStatus = 'default',
  isBold = false,
  leadingVisual: LeadingVisual,
  trailingVisual: TrailingVisual,
  testId,
  sx,
  ...props
}: RuleFilterTagProps & SxProp) => {
  const hasLeadingVisual = Boolean(LeadingVisual)
  const hasTrailingVisual = Boolean(TrailingVisual)
  return (
    <Wrapper as="span" data-testid={testId} isBold={isBold} validationStatus={validationStatus} sx={sx}>
      {' '}
      {hasLeadingVisual ? (
        <InnerVisualSlot isBold={isBold} validationStatus={validationStatus}>
          {typeof LeadingVisual === 'function' ? <LeadingVisual /> : LeadingVisual}
        </InnerVisualSlot>
      ) : null}
      <Label isBold={isBold} validationStatus={validationStatus}>
        {children}
      </Label>
      {hasTrailingVisual ? (
        <InnerVisualSlot isBold={isBold} validationStatus={validationStatus}>
          {typeof TrailingVisual === 'function' ? <TrailingVisual /> : TrailingVisual}
        </InnerVisualSlot>
      ) : null}
    </Wrapper>
  )
}

const Wrapper = styled(Box)<BoxProps & SxProp & RuleFilterTagProps>`
  display: inline-flex;
  border-radius: ${themeGet('radii.2')};
  padding-inline: ${themeGet('space.2')};
  padding-inline-end: ${themeGet('space.1')};
  /* padding-block: ${themeGet('space.1')}; */
  gap: ${themeGet('space.1')};
  align-items: center;
  background-color: ${p =>
    p.validationStatus === 'default' && p.isBold
      ? themeGet('colors.background.neutral.bold.default')
      : p.validationStatus === 'success' && p.isBold
      ? themeGet('colors.background.information.bold.default')
      : p.validationStatus === 'error' && p.isBold
      ? themeGet('colors.background.danger.bold.default')
      : p.validationStatus === 'warning' && p.isBold
      ? themeGet('colors.background.warning.bold.default')
      : p.validationStatus === 'default'
      ? themeGet('colors.background.neutral.default.[default]')
      : p.validationStatus === 'success'
      ? themeGet('colors.background.information.default.[default]')
      : p.validationStatus === 'error'
      ? themeGet('colors.background.danger.default.[default]')
      : p.validationStatus === 'warning'
      ? themeGet('colors.background.warning.default.[default]')
      : 'revert'};
  ${sx};
`
const InnerVisualSlot = styled(Box)<SxProp & TextProps & RuleFilterTagProps>`
  display: block;

  color: ${p =>
    p.validationStatus === 'default' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'success' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'error' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'warning' && p.isBold
      ? themeGet('colors.text.warning.inverse')
      : p.validationStatus === 'default'
      ? themeGet('colors.text.default')
      : p.validationStatus === 'success'
      ? themeGet('colors.text.information')
      : p.validationStatus === 'error'
      ? themeGet('colors.text.danger')
      : p.validationStatus === 'warning'
      ? themeGet('colors.text.warning.default')
      : 'revert'};
  svg {
    // for visual alignment
    /* margin-bottom: -1px; */
  }
  ${sx};
`
const Label = styled(Text)<SxProp & TextProps & RuleFilterTagProps>`
  font-size: ${themeGet('fontSizes.2')};
  /* font-weight: ${themeGet('fontWeights.bold')}; */
  line-height: ${themeGet('lineHeights.default')};
  width: 100%;
  color: ${p =>
    p.validationStatus === 'default' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'success' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'error' && p.isBold
      ? themeGet('colors.text.inverse')
      : p.validationStatus === 'warning' && p.isBold
      ? themeGet('colors.text.warning.inverse')
      : p.validationStatus === 'success'
      ? themeGet('colors.text.default')
      : p.validationStatus === 'error'
      ? themeGet('colors.text.danger')
      : p.validationStatus === 'warning'
      ? themeGet('colors.text.warning.default')
      : 'revert'};
  ${sx};
`
RuleFilterTag.displayName = 'RuleFilterTag'
